<template>
  <div>
    <div class="card" header-bg-variant="white">
      <div class="card-body">
        <div class="row mb-1 ml-0">
          <i class="far fa-credit-card colorOrange"></i>
          <strong class="ml-2" data-test-hook="account-holder-payment-information-account-settings">
            {{ $t('xPaymentInformation') }}
          </strong>
          <popper trigger="hover" :options="{ placement: 'right' }" class="ml-4">
            <div class="popper">
              {{ $t('xPrimaryPaymentInformationMessage') }}
            </div>
            <i slot="reference" class="far fa-question-circle "></i>
          </popper>
        </div>
        <div v-if="paymentInfo" class="row mt-1">
          <div class="col-auto">
            <p data-test-hook="account-holder-payment-information-card-account-settings">
              {{ paymentInfo.cardType.toUpperCase() + ' - ' + paymentInfo.cardSuffix }}
            </p>
          </div>
        </div>
        <div v-if="paymentInfo" class="row">
          <div class="col-auto">
            <p data-test-hook="account-holder-payment-information-expiration-account-settings">
              {{ $t('xExpires') + ' ' + displayMonth(paymentInfo.expirationMonth) + '/' + paymentInfo.expirationYear }}
            </p>
          </div>
        </div>
        <div v-if="shouldShowUpdatePayment" class="row">
          <div class="col">
            <button
              type="button"
              class="btn btn-outline-warning mt-3"
              @click="openModal('update-payment-method')"
              :title="$t('xUpdatePaymentInformation')"
              data-test-hook="update-payment-information-button-account-settings"
            >
              {{ $t('xUpdatePaymentInformation') }}
            </button>
          </div>
        </div>
        <div v-if="shouldShowRemovePayment" class="row">
          <div class="col">
            <button
              type="button"
              class="btn btn-outline-danger rounded-pill mt-3"
              @click="removePaymentInformation()"
              :title="$t('xRemovePaymentInformation')"
              data-test-hook="remove-payment-information-button-account-settings"
            >
              <i class="fas fa-trash-alt colorOrange"></i>
              {{ $t('xRemovePaymentInformation') }}
              <span class="spinner-border ml-2" v-if="removing"></span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <Modal modalId="update-payment-method" size="modal-md" @hidden="closePaymentModal()">
      <ModalHeader :title="$t('xUpdatePaymentMethod')" icon="fal fa-credit-card colorOrange mr-1">
        <div class="cols-auto">
          <button
            type="button"
            class="btn btn-icon-button btn-icon-button--gray"
            @click="closePaymentModal(), (useExistingBillingInformation = true)"
            data-test-hook="CloseModalBtn"
          >
            <i class="fas fa-times" />
          </button>
        </div>
      </ModalHeader>
      <ModalBody>
        <div role="alert" v-if="showValidationBanner" class="alert alert-danger" data-test-hook="ValidationAlert">
          <p>{{ $t('xExperiencedTheseErrors') }}</p>
          <ul v-for="(message, index) in errorMessages" :key="index">
            <li :id="index" data-test-hook="ValidationMsg">
              {{ message }}
            </li>
          </ul>
        </div>
        <div class="row">
          <div class="col-12">
            <h3>
              <strong data-test-hook="CcInfoHeader">{{ $t('xCreditCardInfo') }}</strong>
              <span class="body-text-color">*</span>
            </h3>
          </div>
        </div>
        <div class="row mb-3">
          <div class="col">
            <i class="asterisk-text" data-test-hook="UpdatePaymentWarningMsg">
              {{ $t('xUpdatingPaymentWarning') }}
            </i>
          </div>
        </div>
        <div class="row">
          <div class="col-12">
            <div class="form-group" id="credit-card-number">
              <input
                class="form-control"
                id="creditCardNumber"
                v-model="$v.creditCardNumber.$model"
                maxlength="16"
                type="text"
                :class="status($v.creditCardNumber)"
                v-mask="'################'"
                :placeholder="$t('xCreditCardNumber')"
                aria-describedby="card-number-invalid"
                data-test-hook="CcNumber"
              />
              <div class="invalid-feedback" id="card-number-invalid" data-test-hook="invalidCcNo">
                {{ $t('xCCFieldValidation') }}
              </div>
            </div>
          </div>
        </div>
        <div class="row">
          <div class="col col-lg-6 col-md-6 col-sm-12">
            <div class="form-group" id="expiry-date-month">
              <input
                class="form-control"
                id="xNm"
                v-model="$v.expirationMonth.$model"
                maxlength="2"
                type="text"
                :class="status($v.expirationMonth)"
                v-mask="'##'"
                :placeholder="$t('xMm')"
                aria-describedby="expiry-month-invalid"
                data-test-hook="CcExpireMonth"
              />
              <div class="invalid-feedback" id="expiry-month-invalid" data-test-hook="invalidCcMth">
                {{ $t('xExpirationMonthValidation') }}
              </div>
            </div>
          </div>
          <div class="col col-lg-6 col-md-t6 col-sm-12">
            <div class="form-group" id="expiry-date-year">
              <input
                class="form-control"
                v-model="$v.expirationYear.$model"
                type="text"
                :placeholder="$t('xYy')"
                maxlength="2"
                v-mask="'##'"
                :class="status($v.expirationYear)"
                aria-describedby="expiry-year-invalid"
                data-test-hook="CcExpireYear"
              />
              <div class="invalid-feedback" id="expiry-year-invalid" data-test-hook="invalidCcYear">
                {{ $t('xExpirationYearValidation') }}
              </div>
            </div>
          </div>
          <div class="col-12">
            <div class="input-group" id="cvv">
              <input
                class="form-control"
                v-model="$v.cvv.$model"
                type="text"
                v-mask="'####'"
                maxlength="4"
                :placeholder="$t('xCvv')"
                :class="status($v.cvv)"
                aria-describedby="cvv-invalid"
                data-test-hook="CcCvv"
              />
              <div class="input-group-append">
                <div class="input-group-text">
                  <img :height="20" :src="require('@/assets/images/3-digit-code.svg')" data-test-hook="ccCvvImage" />
                </div>
              </div>
              <div class="invalid-feedback" id="cvv-invalid" data-test-hook="invalidCcCvv">
                {{ $t('xCVVValidation') }}
              </div>
            </div>
          </div>
        </div>
        <div class="mt-4" v-if="accountData">
          <div class="row mb-2">
            <div class="col">
              <div class="font-weight-bold custom-control custom-checkbox">
                <input
                  id="useExistingBillingInformation"
                  type="checkbox"
                  class="custom-control-input"
                  v-model="useExistingBillingInformation"
                  :disabled="savingAccountHolder"
                  data-test-hook="UseCurrentBillingAddressChkBox"
                />
                <label
                  class="custom-control-label"
                  for="useExistingBillingInformation"
                  data-test-hook="useCurrBillingInfoText"
                >
                  {{ $t('xUseCurrentBillingInfo') }}
                </label>
              </div>
            </div>
          </div>
          <div class="row" v-if="useExistingBillingInformation">
            <div class="col-12" data-test-hook="ExistingBillingName">
              {{ accountProfile.firstName }} {{ accountProfile.lastName }}
            </div>
            <div class="col-12" data-test-hook="ExistingBillingAddress">
              {{ DisplayAddress(accountProfile.address) }}
            </div>
          </div>
          <div v-if="!useExistingBillingInformation">
            <div class="row">
              <div class="col-12">
                <fieldset id="firstName-group" class="form-group">
                  <div>
                    <input
                      class="form-control"
                      type="text"
                      id="firstName"
                      v-model="accountData.firstName"
                      data-test-hook="firstName"
                    />
                  </div>
                </fieldset>
              </div>
            </div>
            <div class="row">
              <div class="col-12">
                <fieldset class="form-group" id="lastName-group">
                  <div>
                    <input
                      class="form-control"
                      id="lastName"
                      v-model="accountData.lastName"
                      type="text"
                      data-test-hook="lastName"
                    />
                  </div>
                </fieldset>
              </div>
            </div>
            <div class="row">
              <div class="col">
                <TypeAheadAddress
                  ref="typeAheadAddressSubscription"
                  :currentAddress="accountData.address"
                  @newAddress="setNewBillingAddress"
                />
              </div>
            </div>
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        <button
          type="button"
          class="btn btn-outline-warning"
          @click="closeModal('update-payment-method'), closePaymentModal(), (useExistingBillingInformation = true)"
          data-test-hook="CancelBtn"
        >
          {{ $t('xCancel') }}
        </button>
        <button
          type="button"
          v-if="useExistingBillingInformation"
          class="btn btn-primary"
          @click="payForProduct()"
          :disabled="!canPay()"
          data-test-hook="SaveChangesBtn"
        >
          {{ $t('xSave') }}
          <span class="spinner-border ml-2" v-if="paying"></span>
        </button>
        <button
          v-else
          type="button"
          class="btn btn-warning"
          @click=";(savingAccountHolder = true), saveAccountHolder()"
          :disabled="savingAccountHolder"
          data-test-hook="SaveChangesBtn"
        >
          {{ $t('xConfirmBillingInfo') }}
          <span class="spinner-border ml-2" v-if="savingAccountHolder"></span>
        </button>
      </ModalFooter>
    </Modal>
  </div>
</template>
<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import { validationMixin } from 'vuelidate'
import { required, minLength, maxLength, numeric } from 'vuelidate/lib/validators'
import TypeAheadAddress from '@/components/TypeAheadAddress'
import { isAddressValid } from '../../../utils/address'

const monthInRange = (value, vm) => value >= 1 && value <= 12

export default {
  components: {
    TypeAheadAddress
  },
  mixins: [validationMixin],
  data() {
    return {
      accountData: null,
      creditCardNumber: null,
      cvv: null,
      expirationMonth: null,
      expirationYear: null,
      paying: false,
      paymentInfo: null,
      removing: false,
      savingAccountHolder: false,
      shouldShowRemovePayment: false,
      shouldShowUpdatePayment: false,
      showValidationBanner: false,
      useExistingBillingInformation: true
    }
  },
  //   props: ['paymentInfo'],
  validations: {
    cvv: {
      required,
      minLength: minLength(3),
      maxLength: maxLength(4),
      numeric
    },
    expirationMonth: {
      required,
      minLength: minLength(2),
      maxLength: maxLength(2),
      numeric,
      monthInRange
    },
    expirationYear: {
      required,
      minLength: minLength(2),
      maxLength: maxLength(2),
      numeric
    },
    creditCardNumber: {
      required,
      min: minLength(15),
      max: maxLength(16),
      minLength: minLength(15),
      maxLength: maxLength(16),
      numeric
    },
    consent: {
      required,
      Boolean
    }
  },
  computed: {
    ...mapState({
      accountProfile: state => state.account.accountProfile
    })
  },
  methods: {
    canPay() {
      if (!this.creditCardNumber || !this.cvv || !this.expirationMonth || !this.expirationYear) {
        return false
      }
      if (!this.$v.$anyError && !this.paying) {
        return true
      } else return false
    },
    displayMonth(month) {
      let monthString = String(month)
      if (monthString.length === 1) return '0' + month
      else return month
    },
    async removePaymentInformation() {
      this.removing = true
      let removePaymentModel = {
        organizationId: this.accountProfile.organizationId,
        paymentId: this.paymentInfo.id
      }
      this.$store
        .dispatch('removeBillingAccountPaymentMethod', removePaymentModel)
        .then(response => {
          this.paymentInfo = null
          this.$store.dispatch('displayToast', {
            title: this.$t('xSuccess'),
            message: this.$t('xPaymentMethodRemoved'),
            variant: 'success'
          })
          this.removing = false
        })
        .catch(error => {
          this.$store.dispatch('displayToast', {
            title: this.$t('xRemoveFailed'),
            message: this.$t('xUnableToRemovePaymentInfo'),
            variant: 'danger'
          })
          this.removing = false
        })
    },
    closePaymentModal() {
      this.creditCardNumber = null
      this.expirationMonth = null
      this.expirationYear = null
      this.cvv = null
      this.paying = false
      this.showValidationBanner = false
      this.errorMessages = []
      this.$v.$reset()
      this.closeModal('update-payment-method')
    },
    payForProduct() {
      this.paying = true
      let addPaymentModel = {
        organizationId: this.accountProfile.organizationId,
        cardNumber: this.creditCardNumber,
        cvv: this.cvv,
        expirationMonth: this.expirationMonth,
        expirationYear: '20' + this.expirationYear
      }
      this.$store
        .dispatch('addBillingAccountPaymentMethod', addPaymentModel)
        .then(response => {
          this.paying = false
          this.closeModal('update-payment-method')
          this.closePaymentModal()
          this.$store
            .dispatch('getActivePaymentMethod', this.accountProfile.organizationId)
            .then(paymentResponse => {
              if (paymentResponse.data) {
                this.paymentInfo = paymentResponse.data
                this.useExistingPaymentInfo = true
                this.billingInfoLoading = false
              } else {
                this.paymentInfo = null
                this.useExistingPaymentInfo = false
                this.billingInfoLoading = false
              }
            })
            .catch(error => {
              this.paymentInfo = null
              this.useExistingPaymentInfo = false
              this.billingInfoLoading = false
            })
        })
        .catch(error => {
          this.paying = false
          this.errorMessages = []
          if (error.response.data.errors) {
            if (error.response.data.errors.CardNumber || error.response.data.errors.cardNumber) {
              this.showValidationBanner = true
              this.errorMessages.push(this.$t('xCCIsInvalid'))
            }
            if (error.response.data.errors.Cvv || error.response.data.errors.cvv) {
              this.showValidationBanner = true
              this.errorMessages.push(this.$t('xCvvInvalid'))
            }
            if (error.response.data.errors.ExpirationMonth || error.response.data.errors.expirationMonth) {
              this.showValidationBanner = true
              this.errorMessages.push(this.$t('xExpirationMonthInvalid'))
            }
            if (error.response.data.errors.ExpirationYear || error.response.data.errors.expirationYear) {
              this.showValidationBanner = true
              this.errorMessages.push(this.$t('xExpirationYearInvalid'))
            }
            if (error.response.data.errors.billing || error.response.data.errors.Billing) {
              this.$store.dispatch('displayToast', {
                title: this.$t('xError'),
                message: error.response.data.errors.billing
                  ? error.response.data.errors.billing
                  : error.data.response.errors.Billing,
                variant: 'danger'
              })
            }
          } else {
            this.$store.dispatch('displayToast', {
              title: this.$t('xError'),
              message: this.$t('xFailedToAddPaymentMethod'),
              variant: 'danger'
            })
          }
        })
    },
    isAccountDataValid() {
      return !!(this.accountData.firstName && this.accountData.lastName && isAddressValid(this.accountData.address))
    },
    setNewBillingAddress(newAddress) {
      this.accountData.address = newAddress
      if (this.$refs.typeAheadAddressSubscription.$v.$anyError) {
        this.addressSaveDisabled = true
      } else {
        this.addressSaveDisabled = false
      }
    },
    saveAccountHolder() {
      if (this.$refs?.typeAheadAddressSubscription) {
        this.$refs.typeAheadAddressSubscription.saveAttempted = true
        this.$refs.typeAheadAddressSubscription.$v.$touch()
      }
      if (!this.isAccountDataValid()) {
        this.savingAccountHolder = false
        return
      }
      this.savingAccountHolder = true
      this.$store
        .dispatch('updateAccountHolder', {
          firstName: this.accountData.firstName,
          lastName: this.accountData.lastName,
          organizationAddress: {
            ...this.accountData.address,
            organizationId: this.accountData.organizationId
          }
        })
        .then(response => {
          if (!this.isImpersonating) this.$store.dispatch('getUser')
          else this.$store.dispatch('getImpersonatedUser')
          this.$store.dispatch('getAccountProfile')
          this.timeTheAccountChange()
        })
        .catch(error => {
          this.$store.dispatch('displayToast', {
            title: this.$t('xError'),
            message: this.$t('xFailedToUpdateBillingNameAndAddress'),
            variant: 'danger'
          })
        })
    },
    timeTheAccountChange() {
      setTimeout(
        function() {
          this.savingAccountHolder = false
          this.useExistingBillingInformation = true
        }.bind(this),
        5000
      )
    },
    async getPaymentMethod() {
      await this.$store
        .dispatch('getActivePaymentMethod', this.accountProfile.organizationId)
        .then(response => {
          this.paymentInfo = response.data ? response.data : null
          this.billingInfoLoading = false
        })
        .catch(error => {
          this.paymentInfo = null
          this.billingInfoLoading = false
        })
    }
  },
  async mounted() {
    if (this.accountProfile) {
      this.accountData = _.cloneDeep(this.accountProfile)
    } else {
      this.$store.dispatch('getAccountProfile')
    }

    await this.getPaymentMethod()
    await this.$store
      .dispatch('getAllApparatusSubscriptions', {
        organizationId: this.accountProfile.organizationId
      })
      .then(response => {
        if (response.data?.activeProduct.length > 0) this.shouldShowUpdatePayment = true
        else this.shouldShowRemovePayment = true
        this.legacySubscriptionList = response.data?.activeProduct
        this.subscriptionListLoading = false
      })
      .catch(() => {})
  }
}
</script>
